import type { UserStaff } from "../../cbi-generator/generated/dto/UserStaff";
import { useClient } from "./useClient";
import { useNotificationStore } from "@/stores/notification";
import { useEnv } from "./useEnv";
import type { PersonMember } from "../../cbi-generator/generated/dto/PersonMember";
import { useAuthStore } from "@/stores/auth";
import * as Sentry from "@sentry/vue";
import { memberOrderSessionKey } from "@/composables/shop/memberOrderSessionKey";
import type { MemberOrderSession } from "@/composables/shop/useMemberOrder";

const env = useEnv();
//const memberApi = useMemberApi()

const currentUserRef = shallowRef<UserStaff>();
const currentPersonRef = shallowRef<PersonMember>();
const currentChildrenRef = shallowRef<PersonMember[]>([]);

const fetching = shallowRef<Promise<void>>();

const loading = computed<boolean>(() => fetching.value !== undefined);

export const useUser = () => {
  const memberOrder = inject<MemberOrderSession | null>(
    memberOrderSessionKey,
    null,
  );
  const notification = useNotificationStore();
  const authStore = useAuthStore();
  const client = useClient();
  const loadUser = async (forceFetch: boolean = false) => {
    const hasUserOrPerson = !!currentUserRef.value || !!currentPersonRef.value;
    console.log("HAS PERSON", hasUserOrPerson);
    if (!hasUserOrPerson || forceFetch) {
      try {
        if (fetching.value === undefined) {
          const generation = authStore.identityGeneration;
          fetching.value = (async () => {
            if (env.mode === "MEMBER") {
              const [{ PersonApiClient }, { PersonFrontendService }] =
                await Promise.all([
                  import("../../cbi-generator/frontend/api/PersonApiClient"),
                  import("../../cbi-generator/frontend/services/PersonFrontendService"),
                ]);
              const person = await new PersonFrontendService(
                new PersonApiClient(client),
              ).currentPersonMember();
              const children = await person.getChildren();
              if (generation !== authStore.identityGeneration) return;
              currentPersonRef.value = person;
              currentChildrenRef.value = children;
              Sentry.setUser({
                id: String(person.personId),
                username: person.email ?? undefined,
              });
            } else {
              const [{ UserApiClient }, { UserFrontendService }] =
                await Promise.all([
                  import("../../cbi-generator/frontend/api/UserApiClient"),
                  import("../../cbi-generator/frontend/services/UserFrontendService"),
                ]);
              const user = await new UserFrontendService(
                new UserApiClient(client),
              ).currentUserStaff();
              if (generation !== authStore.identityGeneration) return;
              currentUserRef.value = user;
            }
          })();
        }
        await fetching.value;
      } catch (error: any) {
        console.log("FAILURE");
        notification.addSnackbar("Failed to fetch user info", "error");
        throw error;
      } finally {
        fetching.value = undefined;
      }
    }
  };
  watch(
    () => memberOrder?.isComplete.value,
    (complete, wasComplete) => {
      if (complete && !wasComplete) void loadUser(true);
    },
  );
  authStore.watchLoggedIn(async (loggedIn) => {
    if (loggedIn) {
      const hasUserOrPerson =
        !!currentUserRef.value || !!currentPersonRef.value;
      console.log("HAS PERSON", hasUserOrPerson);
      await loadUser();
    } else {
      fetching.value = undefined;
      currentPersonRef.value = undefined;
      currentChildrenRef.value = [];
      currentUserRef.value = undefined;
    }
  });

  const updatePerson = (person: PersonMember) => {
    currentPersonRef.value = person;
  };

  const watchPerson = (callback: (person?: PersonMember) => void) => {
    watch(currentPersonRef, () =>
      callback(currentPersonRef.value || undefined),
    );
  };

  const awaitFetch = async () => {
    if (fetching.value) await fetching.value;
  };

  // Retained for older one-shot consumers; new async code should use the refs after loadUser().
  const currentPerson = currentPersonRef.value || null;
  const currentUser = currentUserRef.value || null;

  return {
    currentUser,
    currentUserRef,
    currentPerson,
    currentPersonRef,
    currentChildrenRef,
    loading,
    loadUser,
    awaitFetch,
    updatePerson,
    watchPerson,
  };
};
